import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Pencil,
  Wallet,
  Trash2,
  BookOpen,
  Download,
} from "lucide-react";
import { supabase } from "../../../Services/supabaseClient";
import { useCompany } from "../../../context/CompanyContext";
import "./fixedAssets.css";
import { formatNaira, round2 } from "./faMoney";
import DisposeAssetModal from "./DisposeAssetModal";
import PostDepreciationModal from "./PostDepreciationModal";
import AssetAttachments from "./AssetAttachments";
import PostAcquisitionModal from "./PostAcquisitionModal";

function csvEscape(v) {
  const s = String(v ?? "");
  if (s.includes(",") || s.includes('"') || s.includes("\n"))
    return `"${s.replaceAll('"', '""')}"`;
  return s;
}
function downloadCSV(filename, rows) {
  const csv = rows.map((r) => r.map(csvEscape).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function groupKey(j) {
  return `${j.source_type || "none"}|${j.source_id || "none"}|${
    j.date || "0000-00-00"
  }`;
}

function groupTitle(sourceType) {
  if (sourceType === "fixed_asset_acquisition") return "Acquisition";
  if (sourceType === "fixed_asset_depreciation") return "Depreciation";
  if (sourceType === "fixed_asset_disposal") return "Disposal";
  return "Journal";
}

export default function FixedAssetDetails() {
  const { companyId } = useCompany();
  const { assetId } = useParams();
  const nav = useNavigate();

  const [asset, setAsset] = useState(null);
  const [depLines, setDepLines] = useState([]);
  const [disposals, setDisposals] = useState([]);
  const [journals, setJournals] = useState([]);
  const [coa, setCoa] = useState([]);

  const [busy, setBusy] = useState(true);

  const [showDispose, setShowDispose] = useState(false);
  const [showPostDep, setShowPostDep] = useState(false);
  const [showPostAcq, setShowPostAcq] = useState(false);

  async function load() {
    if (!companyId || !assetId) return;
    setBusy(true);
    let allJournals = [];

    const { data: a, error: aErr } = await supabase
      .from("fixed_assets")
      .select("*")
      .eq("company_id", companyId)
      .eq("id", assetId)
      .single();

    if (aErr) {
      alert(aErr.message);
      nav("/app/accounting/fixed-assets");
      return;
    }

    const { data: lines, error: lErr } = await supabase
      .from("fixed_asset_depreciation_lines")
      .select("id, depreciation_amount, posting_date")
      .eq("company_id", companyId)
      .eq("asset_id", assetId)
      .order("posting_date", { ascending: true });

    if (lErr) alert(lErr.message);

    const { data: dsp, error: dErr } = await supabase
      .from("fixed_asset_disposals")
      .select("id, disposal_date, proceeds, notes")
      .eq("company_id", companyId)
      .eq("asset_id", assetId)
      .order("disposal_date", { ascending: false });

    if (dErr) alert(dErr.message);

    // Acquisition journals (source_id = asset_id)
    const { data: j3a, error: j3aErr } = await supabase
      .from("journal_entries")
      .select(
        "id, date, description, money_in, money_out, account_id, source_type, source_id, is_reversal"
      )
      .eq("company_id", companyId)
      .eq("source_type", "fixed_asset_acquisition")
      .eq("source_id", assetId)
      .order("date", { ascending: true });

    if (!j3aErr) allJournals = allJournals.concat(j3a || []);

    // COA (safe fallback: no code ordering if column absent)
    const withCode = await supabase
      .from("chart_of_accounts")
      .select("id,name,type,category,account_number")
      .eq("company_id", companyId)
      .order("account_number", { ascending: true });

    let coaRows = [];
    if (!withCode.error) coaRows = withCode.data || [];
    else {
      const withoutCode = await supabase
        .from("chart_of_accounts")
        .select("id,name,type,category")
        .eq("company_id", companyId);

      if (!withoutCode.error)
        coaRows = (withoutCode.data || []).sort((x, y) =>
          String(x.name).localeCompare(String(y.name))
        );
    }

    const depIds = (lines || []).map((x) => x.id);
    const dispIds = (dsp || []).map((x) => x.id);

    // Depreciation journals (source_id = depreciation_line.id)
    if (depIds.length > 0) {
      const { data: j1, error: j1Err } = await supabase
        .from("journal_entries")
        .select(
          "id, date, description, money_in, money_out, account_id, source_type, source_id, is_reversal"
        )
        .eq("company_id", companyId)
        .eq("source_type", "fixed_asset_depreciation")
        .in("source_id", depIds)
        .order("date", { ascending: true });

      if (!j1Err) allJournals = allJournals.concat(j1 || []);
    }

    // Disposal journals (source_id = disposal.id)
    if (dispIds.length > 0) {
      const { data: j2, error: j2Err } = await supabase
        .from("journal_entries")
        .select(
          "id, date, description, money_in, money_out, account_id, source_type, source_id, is_reversal"
        )
        .eq("company_id", companyId)
        .eq("source_type", "fixed_asset_disposal")
        .in("source_id", dispIds)
        .order("date", { ascending: true });

      if (!j2Err) allJournals = allJournals.concat(j2 || []);
    }

    // Acquisition journals (source_id = asset_id) (kept as-is)
    {
      const { data: j3, error: j3Err } = await supabase
        .from("journal_entries")
        .select(
          "id, date, description, money_in, money_out, account_id, source_type, source_id, is_reversal"
        )
        .eq("company_id", companyId)
        .eq("source_type", "fixed_asset_acquisition")
        .eq("source_id", assetId)
        .order("date", { ascending: true });

      if (!j3Err) allJournals = allJournals.concat(j3 || []);
    }

    // ✅ FIX: de-dupe journals by id to avoid duplicate React keys and duplicate rows
    const byId = new Map();
    for (const j of allJournals) {
      if (!j?.id) continue;
      if (!byId.has(j.id)) byId.set(j.id, j);
    }
    const uniqJournals = Array.from(byId.values());

    setAsset(a);
    setDepLines(lines || []);
    setDisposals(dsp || []);
    setJournals(uniqJournals);
    setCoa(coaRows);
    setBusy(false);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, assetId]);

  const accountLabelById = useMemo(() => {
    const m = new Map();
    for (const a of coa) {
      const code = a.account_number ? `${a.account_number} - ` : "";
      m.set(a.id, `${code}${a.name}`);
    }
    return m;
  }, [coa]);

  const totals = useMemo(() => {
    const cost = Number(asset?.cost || 0);
    const accum = round2(
      depLines.reduce((s, r) => s + Number(r.depreciation_amount || 0), 0)
    );
    const nbv = round2(Math.max(0, cost - accum));
    return { cost, accum, nbv };
  }, [asset, depLines]);

  const hasAcquisitionJournal = useMemo(() => {
    return journals.some(
      (j) =>
        j.source_type === "fixed_asset_acquisition" &&
        String(j.source_id) === String(assetId) &&
        !j.is_reversal
    );
  }, [journals, assetId]);

  const hasAcqReversal = useMemo(() => {
    return journals.some(
      (j) =>
        j.source_type === "fixed_asset_acquisition" &&
        String(j.source_id) === String(assetId) &&
        j.is_reversal
    );
  }, [journals, assetId]);

  const groupedJournals = useMemo(() => {
    const groups = new Map();

    for (const j of journals) {
      const key = groupKey(j);
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          date: j.date,
          source_type: j.source_type,
          source_id: j.source_id,
          entries: [],
          dr: 0,
          cr: 0,
          hasReversal: false,
        });
      }

      const g = groups.get(key);
      g.entries.push(j);
      g.dr += Number(j.money_in || 0);
      g.cr += Number(j.money_out || 0);
      if (j.is_reversal) g.hasReversal = true;
    }

    const list = Array.from(groups.values()).map((g) => ({
      ...g,
      dr: round2(g.dr),
      cr: round2(g.cr),
    }));

    list.sort((a, b) => String(b.date).localeCompare(String(a.date)));
    return list;
  }, [journals]);

  function exportAssetJournalsCSV() {
    if (!asset) return;

    const rows = [
      ["KostPadi Asset Journals Export"],
      ["Asset Tag", asset.asset_tag],
      ["Asset Name", asset.name],
      ["Exported At", new Date().toISOString()],
      [],
      [
        "Group Type",
        "Group Date",
        "Is Reversal Present",
        "Account",
        "Description",
        "Debit",
        "Credit",
      ],
    ];

    for (const g of groupedJournals) {
      const gType = groupTitle(g.source_type);
      const reversalPresent = g.hasReversal ? "YES" : "NO";

      for (const e of g.entries) {
        rows.push([
          gType,
          g.date,
          reversalPresent,
          accountLabelById.get(e.account_id) || e.account_id,
          e.description || "",
          Number(e.money_in || 0),
          Number(e.money_out || 0),
        ]);
      }

      rows.push([gType, g.date, reversalPresent, "TOTAL", "", g.dr, g.cr]);
      rows.push([]);
    }

    downloadCSV(`asset-journals-${asset.asset_tag}-${assetId}.csv`, rows);
  }

  async function deleteAsset() {
    const yes = window.confirm(
      "Delete this asset? This will NOT reverse any already posted journals."
    );
    if (!yes) return;

    const { error } = await supabase
      .from("fixed_assets")
      .delete()
      .eq("company_id", companyId)
      .eq("id", assetId);
    if (error) return alert(error.message);

    nav("/app/accounting/fixed-assets");
  }

  if (busy || !asset) {
    return (
      <div className="fa-wrap">
        <div style={{ opacity: 0.8 }}>Loading…</div>
      </div>
    );
  }

  async function reverseAcquisition() {
    const ok = window.confirm(
      "Reverse acquisition journal for this asset? This will post reversing journal entries."
    );
    if (!ok) return;

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error("Not authenticated.");

      const fnUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/fixed-assets-reverse-acquisition`;
      const res = await fetch(fnUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ company_id: companyId, asset_id: assetId }),
      });

      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Failed to reverse.");

      alert("Acquisition reversed successfully.");
      load();
    } catch (e) {
      alert(e.message || "Error");
    }
  }

  return (
    <div className="fa-wrap">
      <div className="fa-header">
        <div className="fa-title">
          <h2>
            {asset.asset_tag} — {asset.name}
          </h2>
          <p>
            {asset.category || "Fixed Asset"} • Status: <b>{asset.status}</b>
          </p>
        </div>

        <div className="fa-actions">
          <button className="fa-btn" onClick={() => nav(-1)}>
            <ArrowLeft size={16} /> Back
          </button>
          <button className="fa-btn" onClick={() => setShowPostDep(true)}>
            <Wallet size={16} /> Post Depreciation
          </button>
          {!hasAcquisitionJournal && (
            <button className="fa-btn" onClick={() => setShowPostAcq(true)}>
              <Wallet size={16} /> Post Acquisition
            </button>
          )}
          {hasAcquisitionJournal && !hasAcqReversal && (
            <button className="fa-btn" onClick={reverseAcquisition}>
              <Wallet size={16} /> Reverse Acquisition
            </button>
          )}

          <button
            className="fa-btn"
            onClick={() => nav(`/app/accounting/fixed-assets/${assetId}/edit`)}
          >
            <Pencil size={16} /> Edit
          </button>
          <button className="fa-btn" onClick={deleteAsset}>
            <Trash2 size={16} /> Delete
          </button>
          <button
            className="fa-btn"
            onClick={exportAssetJournalsCSV}
            disabled={groupedJournals.length === 0}
          >
            <Download size={16} /> Export CSV
          </button>
          {asset.status === "ACTIVE" && (
            <button
              className="fa-btn fa-btn-primary"
              onClick={() => setShowDispose(true)}
            >
              Dispose
            </button>
          )}
        </div>
      </div>

      <div className="fa-grid">
        <div className="fa-card">
          <div className="label">Cost</div>
          <div className="value">{formatNaira(totals.cost)}</div>
          <div className="hint">
            Purchase: <b>{asset.purchase_date}</b>
          </div>
        </div>
        <div className="fa-card">
          <div className="label">Accumulated Depreciation</div>
          <div className="value">{formatNaira(totals.accum)}</div>
          <div className="hint">
            Lines posted: <b>{depLines.length}</b>
          </div>
        </div>
        <div className="fa-card">
          <div className="label">Net Book Value (NBV)</div>
          <div className="value">{formatNaira(totals.nbv)}</div>
          <div className="hint">
            In service: <b>{asset.in_service_date}</b>
          </div>
        </div>
        <div className="fa-card">
          <div className="label">Useful life</div>
          <div className="value">{asset.useful_life_months} months</div>
          <div className="hint">
            Proration: <b>{asset.prorate_first_month ? "On" : "Off"}</b>
          </div>
        </div>
      </div>

      <div className="fa-panel" style={{ marginBottom: 12 }}>
        <div style={{ fontWeight: 900, marginBottom: 10 }}>
          Depreciation Lines
        </div>
        <table className="fa-table">
          <thead>
            <tr>
              <th>Posting date</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {depLines.map((l) => (
              <tr key={l.id}>
                <td>{l.posting_date}</td>
                <td>{formatNaira(l.depreciation_amount)}</td>
              </tr>
            ))}
            {depLines.length === 0 && (
              <tr>
                <td colSpan={2} style={{ opacity: 0.75 }}>
                  No depreciation posted yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="fa-panel" style={{ marginBottom: 12 }}>
        <div style={{ fontWeight: 900, marginBottom: 10 }}>Disposals</div>
        <table className="fa-table">
          <thead>
            <tr>
              <th>Disposal date</th>
              <th style={{ width: 160 }}>Proceeds</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {disposals.map((d) => (
              <tr key={d.id}>
                <td>{d.disposal_date}</td>
                <td>{formatNaira(d.proceeds)}</td>
                <td style={{ opacity: 0.9 }}>{d.notes || "—"}</td>
              </tr>
            ))}
            {disposals.length === 0 && (
              <tr>
                <td colSpan={3} style={{ opacity: 0.75 }}>
                  No disposals recorded yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="fa-panel">
        <div
          style={{
            fontWeight: 900,
            marginBottom: 10,
            display: "flex",
            gap: 8,
            alignItems: "center",
          }}
        >
          <BookOpen size={18} /> Journals (Grouped)
        </div>

        {groupedJournals.length === 0 ? (
          <div style={{ opacity: 0.75 }}>No linked journals yet.</div>
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            {groupedJournals.map((g) => (
              <div key={g.key} className="fa-card" style={{ padding: 12 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 10,
                    alignItems: "baseline",
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 900 }}>
                      {groupTitle(g.source_type)} • {g.date}
                      {g.hasReversal ? (
                        <span style={{ marginLeft: 8 }} className="fa-badge">
                          Has reversal
                        </span>
                      ) : null}
                    </div>
                    <div style={{ opacity: 0.8, fontSize: 12 }}>
                      Source: {g.source_type} /{" "}
                      {String(g.source_id).slice(0, 8)}…
                    </div>
                  </div>

                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontWeight: 900 }}>
                      Dr {formatNaira(g.dr)} • Cr {formatNaira(g.cr)}
                    </div>
                    <div style={{ opacity: 0.75, fontSize: 12 }}>
                      {round2(g.dr - g.cr) === 0 ? "Balanced" : "Check entries"}
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 10 }}>
                  <table className="fa-table">
                    <thead>
                      <tr>
                        <th>Account</th>
                        <th>Description</th>
                        <th style={{ width: 140 }}>Debit</th>
                        <th style={{ width: 140 }}>Credit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.entries.map((e) => (
                        <tr key={`${g.key}-${e.id}` /* ✅ extra-safe key */}>
                          <td>
                            {accountLabelById.get(e.account_id) || e.account_id}
                          </td>
                          <td style={{ opacity: 0.9 }}>
                            {e.description}
                            {e.is_reversal ? (
                              <span
                                style={{ marginLeft: 8 }}
                                className="fa-badge"
                              >
                                Reversal
                              </span>
                            ) : null}
                          </td>
                          <td>{formatNaira(e.money_in)}</td>
                          <td>{formatNaira(e.money_out)}</td>
                        </tr>
                      ))}
                      <tr style={{ fontWeight: 900 }}>
                        <td colSpan={2}>Group Total</td>
                        <td>{formatNaira(g.dr)}</td>
                        <td>{formatNaira(g.cr)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AssetAttachments companyId={companyId} assetId={assetId} />

      {showDispose && (
        <DisposeAssetModal
          asset={asset}
          onClose={() => setShowDispose(false)}
          onDisposed={() => {
            setShowDispose(false);
            load();
          }}
        />
      )}

      {showPostDep && (
        <PostDepreciationModal
          companyId={companyId}
          onClose={() => setShowPostDep(false)}
          onPosted={() => {
            setShowPostDep(false);
            load();
          }}
        />
      )}

      {showPostAcq && (
        <PostAcquisitionModal
          companyId={companyId}
          asset={asset}
          coa={coa}
          onClose={() => setShowPostAcq(false)}
          onPosted={() => {
            setShowPostAcq(false);
            load();
          }}
        />
      )}
    </div>
  );
}
